// Calls to the AI template generator API (AnimationStudio2_API: Controllers/AIController.cs).
// Every call resolves to the server's JSON ({ status, ... }); network problems resolve to
// { status: false, code: "network", message } so the screens can show a message.

import axios from "axios";
import { postJson } from "../compressedUpload";

function auth() {
  try {
    const data = JSON.parse(localStorage.getItem("animation_studio_2.0_access_token"));
    return { Authorization: "Bearer " + (data && data.access_token) };
  } catch (e) {
    return {};
  }
}

const base = (apiBaseURL) => String(apiBaseURL).replace(/\/$/, "") + "/api/AI/";

async function call(promise) {
  try {
    const resp = await promise;
    return resp.data || { status: false, code: "empty", message: "No answer from the server." };
  } catch (error) {
    const status = error && error.response && error.response.status;
    if (status === 404) return { status: false, code: "not_deployed", message: "AI Studio is not installed on the server yet." };
    if (status === 401) return { status: false, code: "signed_out", message: "Please sign in again." };
    return { status: false, code: "network", message: "Could not reach the server. Check your connection and try again." };
  }
}

const get = (apiBaseURL, path) => call(axios.get(base(apiBaseURL) + path, { headers: auth() }));
const post = (apiBaseURL, path, body = {}) =>
  call(axios.post(base(apiBaseURL) + path, body, { headers: { ...auth(), "Content-Type": "application/json" } }));

export const aiApi = {
  settings: (api) => get(api, "Settings"),
  saveSettings: (api, { mode, provider, apiKey }) => post(api, "Settings", { mode, provider, apiKey: apiKey || null }),
  removeKey: (api) => post(api, "Settings/RemoveKey"),
  /** canvasSize: "16:9" (standard), "9:16" (mobile) or "1:1" (square). */
  generate: (api, { keyword, style, presenter, canvasSize }) =>
    post(api, "Generate", { keyword, style, presenter, canvasSize: canvasSize || "16:9" }),
  status: (api, id) => get(api, "Status/" + id),
  active: (api) => get(api, "Active"),
  history: (api, top = 20) => get(api, "History?top=" + top),
  building: (api, id) => post(api, "Building/" + id),
  fail: (api, id, error) => post(api, "Fail/" + id, { error }),
  cancel: (api, id) => post(api, "Cancel/" + id),
  seen: (api, id) => post(api, "Seen/" + id),
  // The finished project can be several MB (the presenter's animation): sent gzip-compressed.
  complete: (api, id, body) => call(postJson(base(api) + "Complete/" + id, body, { headers: auth() })),
  // Admins: batch mode (many templates at once).
  batch: (api, body) => post(api, "Batch", body),
  batches: (api, top = 6) => get(api, "Batches?top=" + top),
  regenerate: (api, id) => post(api, "Regenerate/" + id),
  publish: (api, templateId, body) => post(api, "Publish/" + templateId, body),
  /** AI Content (editor): { status, text, settings } with the user's AI settings (credits or own key). */
  content: (api, { title }) => post(api, "Content", { title }),
  /** Who has AI Studio: { allowed, show, locked, upgradeUrl, studioUrl } (menu, banner, Create card). */
  access: (api) => get(api, "Access"),
  // Admins: AI Studio access (Admin Panel > AI Studio access).
  accessSettings: (api) => get(api, "Access/Settings"),
  saveAccessSettings: (api, body) => post(api, "Access/Settings", body),
  accessUsers: (api, search = "") =>
    get(api, "Access/Users?top=200" + (search ? "&search=" + encodeURIComponent(search) : "")),
  saveAccessUser: (api, body) => post(api, "Access/Users", body),
  removeAccessUser: (api, email) => post(api, "Access/Users/Remove", { email }),
  checkAccess: (api, email) => get(api, "Access/Check?email=" + encodeURIComponent(email)),
  /** Template categories (Templates page): [{ id, name, ... }]. */
  categories: (api) =>
    call(axios.get(String(api).replace(/\/$/, "") + "/api/Category/GetCategoriesList", { headers: auth() })).then((res) =>
      res && Array.isArray(res.result) ? res.result.filter((c) => c.status !== false) : []
    ),
};

/** The plan of a job (sent as JSON text by the API), or null. */
export function planOf(job) {
  if (!job || !job.plan) return null;
  if (typeof job.plan === "object") return job.plan;
  try {
    return JSON.parse(job.plan);
  } catch (e) {
    return null;
  }
}
